"use client";

import React from "react";
import { ArrowUpRight, CircleCheck as CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON, TERMS } from "@/lib/commonCopy";
import { siteImage } from "@/lib/siteImages";
interface CapabilityItem {
  id: string;
  number: string;
  image: string;
  imageAlt: Record<Lang, string>;
  title: Record<Lang, string>;
  badge: Record<Lang, string>;
  description: Record<Lang, string>;
  point1: Record<Lang, string>;
  point2: Record<Lang, string>;
  point3: Record<Lang, string>;
  linkText: Record<Lang, string>;
  linkTo: string;
  secondaryLinkText?: Record<Lang, string>;
  secondaryLinkTo?: string;
  colSpan: string;
}
const capabilities: CapabilityItem[] = [{
  id: "sourcing",
  number: "01",
  image: siteImage("4e4aa5-wire-mesh-alloy-raw-m-6bbbdf.webp"),
  imageAlt: {
    en: "Stainless steel wire coils and alloy raw materials inspected in warehouse",
    zh: "仓库中待检的不锈钢线材盘圆与合金母料",
    es: "Bobinas de alambre de acero inoxidable y materias primas de aleación inspeccionadas en almacén",
    de: "Edelstahldraht-Coils und Legierungs-Rohmaterialien bei der Eingangsprüfung im Lager",
    fr: "Bobines de fil en acier inoxydable et matières premières d'alliage inspectées en magasin",
    pt: "Bobinas de arame de aço inoxidável e matérias-primas de liga inspecionadas no armazém"
  },
  title: {
    en: "Product Sourcing and Alloy Traceability",
    zh: "原料采购与合金全流程追溯",
    es: "Suministro de productos y trazabilidad de aleaciones",
    de: "Materialbeschaffung und lückenlose Legierungs-Rückverfolgbarkeit",
    fr: "Approvisionnement et traçabilité intégrale des alliages",
    pt: "Fornecimento de produtos e rastreabilidade de ligas"
  },
  badge: {
    en: "Alloy Sourcing",
    zh: "合金供应",
    es: "Suministro de aleaciones",
    de: "Materialbeschaffung",
    fr: "Fourniture d'alliages",
    pt: "Fornecimento de ligas"
  },
  description: {
    en: "Mill-direct sourcing of certified stainless steel and specialty corrosion-resistant alloys with clear metallurgical provenance and production coordination.",
    zh: "直采钢厂认证不锈钢及特种耐腐蚀合金，具备清晰的冶金原产地凭证与生产协同机制。",
    es: "Abastecimiento directo de acería de acero inoxidable certificado y aleaciones especiales resistentes a la corrosión con procedencia metalúrgica contrastada.",
    de: "Direktbezug vom Walzwerk für zertifizierten Edelstahl und korrosionsbeständige Sonderlegierungen mit verlässlichem Herkunftsnachweis.",
    fr: "Approvisionnement direct en usine d'aciers inoxydables certifiés et d'alliages spéciaux résistants à la corrosion avec traçabilité métallurgique.",
    pt: "Abastecimento direto de usina de aço inoxidável certificado e ligas especiais resistentes à corrosão com comprovação metalúrgica."
  },
  point1: {
    en: "Mill-direct sourcing of certified standard and specialty alloys",
    zh: "直采认证不锈钢及特种耐腐蚀合金母料",
    es: "Abastecimiento directo de molino de aleaciones estándar y especiales certificadas",
    de: "Direktbezug zertifizierter Standard- und Sonderlegierungen ab Werk",
    fr: "Approvisionnement direct d'alliages standard et spéciaux certifiés",
    pt: "Abastecimento direto de usina de ligas padrão e especiais certificadas"
  },
  point2: {
    en: "Broad grade availability covering austenitic, duplex, and nickel alloys",
    zh: "覆盖标准奥氏体、双相不锈钢及高性能耐腐蚀牌号",
    es: "Amplia disponibilidad de calidades: austeníticas, dúplex y aleaciones de níquel",
    de: "Breite Werkstoffverfügbarkeit: austenitische Stähle, Duplex- und Nickelbasislegierungen",
    fr: "Large disponibilité de nuances : austénitiques, duplex et alliages de nickel",
    pt: "Ampla disponibilidade de graus: austeníticos, duplex e ligas de níquel"
  },
  point3: {
    en: "Raw material allocation and direct coordination with weaving production",
    zh: "根据定制编织规格进行原料预留与生产排产协同",
    es: "Reserva de materia prima y coordinación directa con la producción de tejido",
    de: "Rohmaterialreservierung und direkte Abstimmung mit der Webproduktion",
    fr: "Attribution des matières premières et coordination directe avec le tissage",
    pt: "Alocação de matéria-prima e coordenação direta com a produção de tecelagem"
  },
  linkText: COMMON.exploreProducts,
  linkTo: "/ProductsPage",
  colSpan: "lg:col-span-3"
}, {
  id: "oem-custom",
  number: "02",
  image: siteImage("4e4aa5-oem-custom-mesh-stamp-3fb7f8.webp"),
  imageAlt: {
    en: "Fabricated wire mesh filter components including stamped discs and cylindrical tubes",
    zh: "精密深加工丝网过滤组件，包括冲压滤片与圆筒过滤网管",
    es: "Componentes filtrantes de malla fabricados, incluidos discos estampados y tubos cilíndricos",
    de: "Konfektionierte Drahtgewebe-Filterelemente einschließlich gestanzter Ronden und Filterzylinder",
    fr: "Composants filtrants confectionnés en toile métallique comprenant disques découpés et cylindres",
    pt: "Componentes filtrantes de malha fabricados, incluindo discos estampados e tubos cilíndricos"
  },
  title: {
    en: "Custom and OEM Mesh Engineering",
    zh: "定制化与 OEM 丝网工程",
    es: "Ingeniería de mallas personalizadas y OEM",
    de: "Kundenspezifische Fertigung & OEM-Drahtgewebetechnik",
    fr: "Ingénierie de toiles métalliques sur mesure et OEM",
    pt: "Engenharia de malhas sob medida e OEM"
  },
  badge: {
    en: "Manufacturing",
    zh: "精密制造",
    es: "Fabricación",
    de: "Fertigung",
    fr: "Fabrication",
    pt: "Fabricação"
  },
  description: {
    en: "Tailored wire mesh geometry and filter component manufacturing designed for original equipment manufacturers and filtration systems builders.",
    zh: "为原始设备制造商 (OEM) 及过滤系统成套商量身定制丝网微观结构与滤芯组件制造。",
    es: "Geometría de malla a medida y fabricación de componentes filtrantes para fabricantes de equipos originales e integradores de sistemas de filtración.",
    de: "Maßgeschneiderte Gewebegeometrien und Filterkomponentenfertigung für Erstausrüster (OEM) und Filtrationsanlagenbauer.",
    fr: "Géométrie de toile sur mesure et fabrication de composants filtrants pour les équipementiers d'origine et intégrateurs de filtration.",
    pt: "Geometria de malha sob medida e fabricação de componentes filtrantes para fabricantes de equipamentos originais e integradores de sistemas de filtração."
  },
  point1: {
    en: "Custom aperture geometry, wire diameters, and micron ratings",
    zh: "定制孔径几何结构、丝径规格与微米级过滤精度",
    es: "Geometría de abertura personalizada, diámetros de alambre y clasificaciones micrométricas",
    de: "Kundenspezifische Maschenweiten, Drahtdurchmesser und Abscheidegrade",
    fr: "Ouvertures sur mesure, diamètres de fil personnalisés et seuils de filtration",
    pt: "Geometria de abertura personalizada, diâmetros de arame e classificações em mícrons"
  },
  point2: {
    en: "Prototype batch testing, dimensional inspection, and CAD drawing sign-off",
    zh: "样件批次测试、精密尺寸检验与 CAD 工程图纸会签",
    es: "Ensayos de lotes de prototipos, inspección dimensional y aprobación de planos CAD",
    de: "Prototypen-Musterprüfung, Maßkontrolle und CAD-Zeichnungsfreigabe",
    fr: "Essais de lots prototypes, contrôle dimensionnel et validation des plans CAD",
    pt: "Testes de lotes protótipos, inspeção dimensional e aprovação de desenhos CAD"
  },
  point3: {
    en: "Forming into stamped discs, pleated packs, cylinders, and welded baskets",
    zh: "深加工为冲压滤片、折波滤芯、圆筒过滤网管及焊接滤篮",
    es: "Conformado en discos estampados, paquetes plisados, cilindros y cestas soldadas",
    de: "Weiterverarbeitung zu Ronden, Plisseepaketen, Zylindern und geschweißten Filterkörben",
    fr: "Façonnage en disques emboutis, packs plissés, cylindres et paniers soudés",
    pt: "Conformação em discos estampados, pacotes plissados, cilindros e cestos soldados"
  },
  linkText: {
    en: "Explore OEM Engineering",
    zh: "探索 OEM 工程",
    es: "Explorar ingeniería OEM",
    de: "OEM-Entwicklung entdecken",
    fr: "Découvrir l'ingénierie OEM",
    pt: "Explorar engenharia OEM"
  },
  linkTo: "/OEMCustomMeshEngineeringPage",
  colSpan: "lg:col-span-3"
}, {
  id: "selection",
  number: "03",
  image: siteImage("4e4aa5-mesh-aperture-metallu-66486d.webp"),
  imageAlt: {
    en: "Optical microscope inspection of precision wire mesh aperture and weave consistency",
    zh: "光学显微镜下精密编织丝网孔径与编织均匀性检验",
    es: "Inspección microscópica óptica de abertura de malla de alambre y consistencia de tejido",
    de: "Lichtmikroskopische Prüfung der Maschenweite und Gewebegleichmäßigkeit von Drahtgewebe",
    fr: "Contrôle au microscope optique de l'ouverture de maille et de la régularité du tissage",
    pt: "Inspeção microscópica óptica da abertura da malha de arame e consistência da tecelagem"
  },
  title: {
    en: "Material and Product Selection Guidance",
    zh: "材料与产品选型工程指导",
    es: "Asesoramiento en selección de materiales y productos",
    de: "Beratung zu Werkstoff- und Produktauswahl",
    fr: "Conseil en sélection des matériaux et produits",
    pt: "Orientação para seleção de materiais e produtos"
  },
  badge: {
    en: "Consultation",
    zh: "技术咨询",
    es: "Consultoría",
    de: "Beratung",
    fr: "Conseil technique",
    pt: "Consultoria"
  },
  description: {
    en: "Engineering guidance to match alloy chemistry, mechanical strength, and weave geometry to your fluid viscosity, operating temperature, and operating pressure.",
    zh: "根据流体粘度、工作温度及工作压力，提供合金化学相容性、机械强度与编织形式的精准选型建议。",
    es: "Asesoramiento de ingeniería para adaptar la química de aleaciones, resistencia mecánica y geometría de tejido a la viscosidad del fluido, temperatura y presión operativas.",
    de: "Ingenieurtechnische Beratung zur Abstimmung von Legierung, Festigkeit und Gewebestruktur auf Viskosität, Betriebstemperatur und Druckverhältnisse.",
    fr: "Conseil technique pour adapter chimie des alliages, résistance mécanique et type de tissage à la viscosité du fluide, à la température et à la pression de service.",
    pt: "Orientação de engenharia para conciliar química de ligas, resistência mecânica e geometria de trama com a viscosidade do fluido, temperatura e pressão operacionais."
  },
  point1: {
    en: "Grade evaluation across 304L, 316L, 904L, Duplex, Hastelloy, and Monel",
    zh: "涵盖 304L、316L、904L、双相钢、哈氏合金及蒙乃尔合金的材质评估",
    es: "Evaluación de calidades en 304L, 316L, 904L, Dúplex, Hastelloy y Monel",
    de: "Werkstoffbewertung für 304L, 316L, 904L, Duplex, Hastelloy und Monel",
    fr: "Évaluation de nuances pour 304L, 316L, 904L, Duplex, Hastelloy et Monel",
    pt: "Avaliação de graus para 304L, 316L, 904L, Duplex, Hastelloy e Monel"
  },
  point2: {
    en: "Aperture sizing for nominal, absolute, and high-efficiency retention",
    zh: "名义精度、绝对精度及高效截留级别的孔隙精准计算",
    es: "Dimensionamiento de abertura para retención nominal, absoluta y de alta eficiencia",
    de: "Maschenweiten-Auslegung für nominale, absolute und hocheffiziente Abscheidung",
    fr: "Dimensionnement d'ouverture pour rétention nominale, absolue et haute efficacité",
    pt: "Dimensionamento de abertura para retenção nominal, absoluta e de alta eficiência"
  },
  point3: {
    en: "Cross-reference technical charts and chemical compatibility matrices",
    zh: "对照技术参数图表与耐腐蚀化学相容性矩阵",
    es: "Cruce con tablas técnicas y matrices de compatibilidad química",
    de: "Abgleich mit technischen Kennfeldtabelle und chemischen Beständigkeitsmatrizen",
    fr: "Croisement avec les tableaux techniques et matrices de compatibilité chimique",
    pt: "Cruzamento com tabelas técnicas e matrizes de compatibilidade química"
  },
  linkText: {
    en: "Product Selection Guide",
    zh: "产品选型指南",
    es: "Guía de selección de productos",
    de: "Produktauswahl-Leitfaden",
    fr: "Guide de sélection des produits",
    pt: "Guia de seleção de produtos"
  },
  linkTo: "/ProductSelectionPage",
  secondaryLinkText: {
    en: "Materials & Grades",
    zh: "材料与材质牌号",
    es: "Materiales y calidades",
    de: "Werkstoffe & Güten",
    fr: "Matériaux et nuances",
    pt: "Materiais e graus"
  },
  secondaryLinkTo: "/MaterialsAndGradesPage",
  colSpan: "lg:col-span-2"
}, {
  id: "supply-chain",
  number: "04",
  image: siteImage("4e4aa5-logistics-mesh-buffer-5356f0.webp"),
  imageAlt: {
    en: "Secure industrial packaging and palletized wire mesh rolls ready for global dispatch",
    zh: "面向全球发运的安全加固工业木箱与托盘包装丝网原卷",
    es: "Embalaje industrial seguro y rollos de malla de alambre paletizados listos para envío internacional",
    de: "Sichere Industrieverpackung und palettierte Drahtgeweberollen für den weltweiten Versand",
    fr: "Emballage industriel renforcé et rouleaux de toile métallique palettisés prêts à l'expédition",
    pt: "Embalagem industrial segura e rolos de malha de arame paletizados prontos para envio global"
  },
  title: {
    en: "Supply Chain and Buffer Stock Coordination",
    zh: "供应链优化与安全备库协调",
    es: "Cadena de suministro y coordinación de stock regulador",
    de: "Lieferkettenoptimierung & Sicherheitslager-Koordination",
    fr: "Chaîne d'approvisionnement et coordination des stocks tampons",
    pt: "Cadeia de suprimentos e coordenação de estoque de segurança"
  },
  badge: {
    en: "Logistics",
    zh: "物流支持",
    es: "Logística",
    de: "Logistik",
    fr: "Logistique",
    pt: "Logística"
  },
  description: {
    en: "Dedicated production scheduling and localized inventory programs to protect continuous manufacturing operations from raw material lead time fluctuations.",
    zh: "专属生产排程与常备库存方案，保障连续化工业生产免受原材料交期波动影响。",
    es: "Programación de producción dedicada y planes de inventario regulador para proteger las operaciones continuas frente a variaciones de plazos.",
    de: "Dedizierte Produktionsplanung und Bevorratungskonzepte zur Absicherung kontinuierlicher Fertigungsprozesse gegen Lieferzeitschwankungen.",
    fr: "Planification de production dédiée et programmes de stocks tampons pour sécuriser les process continus contre les aléas de délais.",
    pt: "Programação de produção dedicada e planos de estoque regulador para proteger operações contínuas contra flutuações de prazos."
  },
  point1: {
    en: "Scheduled release contracts and planned buffer stock allocations",
    zh: "分期发货协议与专属安全缓冲库存配额保障",
    es: "Contratos de entregas programadas y asignación de stock de seguridad",
    de: "Abrufverträge und geplante Bereitstellung von Sicherheitsbeständen",
    fr: "Contrats de cadencement et allocation de stocks tampons dédiés",
    pt: "Contratos de remessas programadas e alocação planejada de estoque regulador"
  },
  point2: {
    en: "Multi-batch delivery alignment and continuous supply coordination",
    zh: "多批次交付协同与长期供应连续性管理",
    es: "Coordinación de entregas por lotes y gestión continua del suministro",
    de: "Mehrchargen-Lieferabstimmung und langfristiges Kontinuitätsmanagement",
    fr: "Coordination des livraisons multi-lots et suivi de la continuité d'approvisionnement",
    pt: "Alinhamento de entregas em múltiplos lotes e coordenação contínua de fornecimento"
  },
  point3: {
    en: "Full Incoterms execution (FOB, CIF, DDP) for North America and Europe",
    zh: "面向欧美及全球市场支持完整的国际贸易术语 (FOB, CIF, DDP)",
    es: "Ejecución completa de Incoterms (FOB, CIF, DDP) para Norteamérica y Europa",
    de: "Vollständige Abwicklung nach Incoterms (FOB, CIF, DDP) für Nordamerika und Europa",
    fr: "Application rigoureuse des Incoterms (FOB, CIF, DDP) pour l'Amérique du Nord et l'Europe",
    pt: "Execução completa de Incoterms (FOB, CIF, DDP) para América do Norte e Europa"
  },
  linkText: {
    en: "Review Quality & Supply",
    zh: "查看质量与供应体系",
    es: "Ver calidad y suministro",
    de: "Qualität & Versorgung prüfen",
    fr: "Consulter qualité et approvisionnement",
    pt: "Ver qualidade e fornecimento"
  },
  linkTo: "/QualitySupplyPage",
  colSpan: "lg:col-span-2"
}, {
  id: "technical-support",
  number: "05",
  image: siteImage("4e4aa5-differential-pressure-52bbb7.webp"),
  imageAlt: {
    en: "Differential pressure flow modeling simulation and fluid test bench schematic",
    zh: "差压流体建模分析与流量测试管路示意图",
    es: "Simulación de modelado de flujo de presión diferencial y banco de pruebas de fluidos",
    de: "Differenzdruck-Strömungssimulation und Prüfstandskonfiguration für Fluide",
    fr: "Modélisation des pertes de charge et banc d'essai hydraulique",
    pt: "Simulação de modelagem de fluxo de pressão diferencial e bancada de teste de fluidos"
  },
  title: {
    en: "Technical Support and Flow Calculations",
    zh: "技术支持与流体压降计算",
    es: "Soporte técnico y cálculos de flujo",
    de: "Technischer Support & Strömungsberechnungen",
    fr: "Support technique et calculs de débit",
    pt: "Suporte técnico e cálculos de vazão"
  },
  badge: {
    en: "Engineering",
    zh: "工程技术",
    es: "Ingeniería",
    de: "Ingenieurwesen",
    fr: "Ingénierie",
    pt: "Engenharia"
  },
  description: {
    en: "Direct access to filtration application engineers for mechanical specification reviews, differential pressure estimations, and mesh validation.",
    zh: "直接对接过滤应用工程师，进行机械规格审查、压降评估与滤网适用性验证。",
    es: "Acceso directo a ingenieros de aplicaciones de filtración para revisión de especificaciones mecánicas, estimación de presión diferencial y validación de mallas.",
    de: "Direkter Draht zu Filtrationsingenieuren für Spezifikationsprüfungen, Differenzdruckabschätzungen und Gewebevalidierung.",
    fr: "Accès direct aux ingénieurs d'application en filtration pour revue des spécifications mécaniques, estimation de perte de charge et validation de toile.",
    pt: "Acesso direto a engenheiros de aplicações de filtração para revisão de especificações mecânicas, estimativas de perda de carga e validação de malha."
  },
  point1: {
    en: "Differential pressure (delta-P) modeling and flow rate calculations",
    zh: "压降 (ΔP) 数值建模与流量通量计算",
    es: "Modelado de presión diferencial (delta-P) y cálculos de caudal",
    de: "Differenzdruck-Modellierung (Delta-P) und Durchflussberechnungen",
    fr: "Modélisation des pertes de charge (delta-P) et calculs de débit",
    pt: "Modelagem de queda de pressão (delta-P) e cálculos de vazão"
  },
  point2: {
    en: "Cleanability, backwash cycle, and mechanical loading evaluations",
    zh: "清洗再生性、反冲洗循环周期与机械承压能力评估",
    es: "Evaluaciones de lavabilidad, ciclos de retrolavado y carga mecánica",
    de: "Bewertung von Abreinigbarkeit, Rückspülzyklen und mechanischer Belastbarkeit",
    fr: "Évaluations de la nettoyabilité, des cycles de rétrolavage et de la tenue mécanique",
    pt: "Avaliações de lavabilidade, ciclos de retrolavagem e resistência mecânica"
  },
  point3: {
    en: "Standard compliance verification against ASTM E2016 and ISO 9044",
    zh: "依据 ASTM E2016 与 ISO 9044 标准进行合规性验证",
    es: "Verificación de cumplimiento normativo frente a ASTM E2016 e ISO 9044",
    de: "Normenkonformitätsprüfung nach ASTM E2016 und ISO 9044",
    fr: "Vérification de conformité aux normes ASTM E2016 et ISO 9044",
    pt: "Verificação de conformidade normativa segundo ASTM E2016 e ISO 9044"
  },
  linkText: {
    en: "Access Engineering Guides",
    zh: "查阅工程指南",
    es: "Ver guías de ingeniería",
    de: "Zu den Ingenieur-Leitfäden",
    fr: "Accéder aux guides d'ingénierie",
    pt: "Acessar guias de engenharia"
  },
  linkTo: "/TechnicalResourcesPage",
  colSpan: "lg:col-span-2"
}];
export default function Capabilities() {
  const {
    lang
  } = useLanguage();
  return <section id="corecapabilities" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl mb-10 lg:mb-14">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-px w-6 bg-primary" />
              <span className="uppercase tracking-widest font-mono text-primary font-semibold mf-caption">
                {pick(lang, {
                en: "Core Capabilities",
                zh: "核心能力",
                es: "Capacidades clave",
                de: "Kernkompetenzen",
                fr: "Compétences clés",
                pt: "Capacidades principais"
              })}
              </span>
            </div>
            <h2 className="font-default mf-h2 text-foreground mb-4">
              {pick(lang, {
              en: "Industrial Wire Mesh Engineering and Supply",
              zh: "工业丝网工程与供应支持",
              es: "Ingeniería y suministro de mallas de alambre industriales",
              de: "Industrie-Drahtgewebetechnik & Lieferkoordination",
              fr: "Ingénierie et approvisionnement en toiles métalliques industrielles",
              pt: "Engenharia e fornecimento de telas de arame industriais"
            })}
            </h2>
            <p className="mf-lead text-foreground/80 font-default">
              {pick(lang, {
              en: "From certified alloy sourcing and custom weave geometry to delta-P flow calculations, we support industrial filtration equipment builders and plant operators across critical process stages.",
              zh: "从认证合金采购、定制编织结构到压降流体计算，我们在关键工艺阶段为工业过滤设备制造商及工厂运维人员提供全方位工程支持。",
              es: "Desde el abastecimiento de aleaciones certificadas y geometrías de tejido a medida hasta cálculos de flujo delta-P, respaldamos a fabricantes de equipos y operadores en etapas críticas del proceso.",
              de: "Von der zertifizierten Legierungsbeschaffung und maßgeschneiderten Gewebekonstruktion bis zu Delta-P-Strömungsberechnungen unterstützen wir Anlagenbauer und Betriebsingenieure bei kritischen Prozessschritten.",
              fr: "De l'approvisionnement en alliages certifiés et du tissage sur mesure jusqu'aux calculs hydrauliques de delta-P, nous accompagnons les constructeurs de filtres et exploitants à chaque étape clé.",
              pt: "Desde o fornecimento de ligas certificadas e geometrias de tecelagem sob medida até cálculos de vazão delta-P, apoiamos fabricantes de equipamentos e operadores industriais em etapas críticas."
            })}
            </p>
          </div>
        </Reveal>

        {/* 5-Item Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
          {capabilities.map((item, i) => <Reveal key={item.id} delay={Math.min(i * 60, 300)} className={`flex flex-col ${item.colSpan}`}>
              <Card className="mf-hover-lift h-full border border-border bg-card flex flex-col justify-between overflow-hidden gap-0 py-0">
                <div>
                  {/* Supporting process imagery */}
                  

                  <CardHeader className="space-y-3 p-5 pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline" className="bg-muted text-foreground font-mono uppercase tracking-wider mf-caption">
                        {pick(lang, item.badge)}
                      </Badge>
                    </div>
                    <CardTitle className="mf-h3 font-default text-card-foreground">
                      {pick(lang, item.title)}
                    </CardTitle>
                    <CardDescription className="text-foreground/75 mf-caption pt-1 leading-relaxed">
                      {pick(lang, item.description)}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="px-5 pt-0 pb-6 space-y-3">
                    <div className="border-t border-border/80 pt-4 space-y-2.5">
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="mf-caption text-foreground/85">
                          {pick(lang, item.point1)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="mf-caption text-foreground/85">
                          {pick(lang, item.point2)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="mf-caption text-foreground/85">
                          {pick(lang, item.point3)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="px-5 py-4 border-t border-border/60 mt-auto flex-wrap gap-2.5">
                  {item.linkText && item.linkTo && <Button variant="outline" size="sm" asChild className="font-medium">
                      <Link to={item.linkTo} className="inline-flex items-center gap-1.5">
                        <span>{pick(lang, item.linkText)}</span>
                        <ArrowUpRight className="size-3.5 opacity-70" />
                      </Link>
                    </Button>}
                  {item.secondaryLinkText && item.secondaryLinkTo && <Button variant="ghost" size="sm" asChild className="font-medium text-foreground/80 hover:text-foreground">
                      <Link to={item.secondaryLinkTo} className="inline-flex items-center gap-1.5">
                        <span>{pick(lang, item.secondaryLinkText)}</span>
                        <ArrowUpRight className="size-3.5 opacity-70" />
                      </Link>
                    </Button>}
                </CardFooter>
              </Card>
            </Reveal>)}
        </div>

        {/* Bottom Technical Assurance Strip */}
        <Reveal delay={240}>
          <div className="mt-12 p-6 rounded-lg bg-muted border border-border flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1 max-w-2xl">
              <h3 className="font-semibold text-foreground font-default uppercase tracking-wider mf-caption">
                {pick(lang, {
                en: "Need custom mesh drawings or urgent specification review?",
                zh: "需要定制丝网图纸或紧急规格评审？",
                es: "¿Necesita planos de malla a medida o revisión urgente de especificaciones?",
                de: "Benötigen Sie Zeichnungen für Sondergewebe oder eine zügige Spezifikationsprüfung?",
                fr: "Besoin de plans de toile sur mesure ou d'un examen technique prioritaire ?",
                pt: "Precisa de desenhos de malha sob medida ou revisão urgente de especificações?"
              })}
              </h3>
              <p className="text-foreground/75 font-default mf-caption">
                {pick(lang, {
                en: "Our application engineering team reviews customer CAD drawings, differential pressure targets, and alloy compatibility parameters for prompt project consultation.",
                zh: "我们的应用工程团队可对客户 CAD 图纸、压降控制目标和合金耐腐蚀参数进行专业评审与选型沟通。",
                es: "Nuestro equipo de ingeniería de aplicaciones revisa planos CAD, objetivos de presión diferencial y parámetros de aleación para una consulta técnica ágil.",
                de: "Unser Anwendungsteam prüft Kunden-CAD-Zeichnungen, Differenzdruckvorgaben und Werkstoffanforderungen für eine gezielte technische Beratung.",
                fr: "Notre équipe d'ingénierie examine vos plans CAD, objectifs de perte de charge et critères de tenue des alliages pour vous conseiller rapidement.",
                pt: "Nossa equipe de engenharia analisa desenhos CAD, metas de pressão diferencial e parâmetros de liga para uma consulta técnica ágil."
              })}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0 flex-wrap">
              <Button variant="default" size="sm" asChild>
                <Link to="/RequestAQuotePage">
                  {pick(lang, {
                  en: "Request Engineering Quote",
                  zh: "索取工程报价",
                  es: "Solicitar cotización técnica",
                  de: "Technisches Angebot anfragen",
                  fr: "Demander un devis technique",
                  pt: "Solicitar orçamento de engenharia"
                })}
                </Link>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/ContactPage">
                  {pick(lang, {
                  en: "Contact Technical Team",
                  zh: "联系技术团队",
                  es: "Contactar al equipo técnico",
                  de: "Technisches Team kontaktieren",
                  fr: "Contacter l'équipe technique",
                  pt: "Falar com a equipe técnica"
                })}
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}